import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getLocalTimezone } from '@src/utils/date';
import { clearAccessToken, getAccessToken } from '@src/utils/token-store';

/**
 * Stamped on every registration so the account has a timezone from the outset —
 * without it the notification scheduler cannot work out when a user's day starts,
 * and geography is unknowable after the fact.
 */
const withTimezone = (body) => {
  const timezone = getLocalTimezone();

  return timezone ? { ...body, timezone } : body;
};

export const authApi = createApi({
  reducerPath: 'authQueries',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers) => {
      const token = getAccessToken();

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    signUp: builder.mutation({
      query: (body) => ({
        url: '/auth/sign-up',
        method: 'POST',
        body: withTimezone(body),
      }),
      async onQueryStarted() {
        clearAccessToken();
      },
    }),
    signIn: builder.mutation({
      query: (credentials) => ({
        url: '/auth/sign-in',
        method: 'POST',
        body: credentials,
      }),
    }),
    googleSignUp: builder.mutation({
      query: (body) => ({
        url: '/auth/google-sign-up',
        method: 'POST',
        body: withTimezone(body),
      }),
      async onQueryStarted() {
        clearAccessToken();
      },
    }),
    googleSignIn: builder.mutation({
      query: (credentials) => ({
        url: '/auth/google-sign-in',
        method: 'POST',
        body: credentials,
      }),
    }),
    facebookSignUp: builder.mutation({
      query: (body) => ({
        url: '/auth/facebook-sign-up',
        method: 'POST',
        body: withTimezone(body),
      }),
      async onQueryStarted() {
        clearAccessToken();
      },
    }),
    facebookSignIn: builder.mutation({
      query: (credentials) => ({
        url: '/auth/facebook-sign-in',
        method: 'POST',
        body: credentials,
      }),
    }),
    refreshToken: builder.mutation({
      query: () => ({
        url: '/auth/refresh-token',
        method: 'POST',
        body: {},
      }),
    }),
    forgotPassword: builder.mutation({
      query: (data) => ({
        url: '/auth/forgot-password',
        method: 'POST',
        body: data,
      }),
    }),
    resetPassword: builder.mutation({
      query: (data) => ({
        url: '/auth/reset-password',
        method: 'POST',
        body: data,
      }),
    }),
    impersonateUser: builder.mutation({
      query: (userId) => ({
        url: `/auth/admin/impersonate/${userId}`,
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useSignUpMutation,
  useSignInMutation,
  useGoogleSignUpMutation,
  useGoogleSignInMutation,
  useFacebookSignUpMutation,
  useFacebookSignInMutation,
  useRefreshTokenMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useImpersonateUserMutation,
} = authApi;
